import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  ButtonGroup,
  Checkbox,
  Spinner,
  Center,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { InfoIcon } from '@chakra-ui/icons';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import supabase from '../supabaseClient';
import {
  VIEW_MODES,
  generateBuckets,
  aggregateSessionCounts,
  buildClientActiveRanges,
  aggregateActiveClients,
  buildChartSeries,
  findEarliestAttendanceDate,
  getCurrentWeekBucketKey,
} from '../utils/sessionTrendsUtils';

const PAGE_SIZE = 1000;

const VIEW_MODE_LABELS = {
  'all-time': 'All Time',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly',
};

const LINE_CONFIG = [
  { key: 'total', label: 'Total', color: '#3182CE', yAxisId: 'sessions' },
  { key: 'present', label: 'Present', color: '#38A169', yAxisId: 'sessions' },
  { key: 'cancelled', label: 'Cancelled', color: '#DD6B20', yAxisId: 'sessions' },
  { key: 'activeClients', label: 'Active Clients', color: '#805AD5', yAxisId: 'clients' },
];

async function fetchAllRows(table, select, orderColumn) {
  const allRows = [];
  let from = 0;

  while (true) {
    const { data, error } = await supabase
      .from(table)
      .select(select)
      .order(orderColumn, { ascending: true })
      .range(from, from + PAGE_SIZE - 1);

    if (error) throw error;

    const page = data || [];
    allRows.push(...page);
    if (page.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  return allRows;
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  return (
    <Box bg="white" borderWidth="1px" borderRadius="md" p={3} shadow="md">
      <Text fontWeight="semibold" mb={2}>{label}</Text>
      {payload.map((entry) => (
        <Text key={entry.dataKey} fontSize="sm" color={entry.color}>
          {entry.name}: {entry.value}
        </Text>
      ))}
    </Box>
  );
}

const SessionTrendsChart = ({ isVisible = true }) => {
  const toast = useToast();
  const [viewMode, setViewMode] = useState('weekly');
  const [visibleLines, setVisibleLines] = useState({
    total: true,
    present: true,
    cancelled: true,
    activeClients: true,
  });
  const [loading, setLoading] = useState(false);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [clients, setClients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);

  const fetchTrendData = useCallback(async () => {
    setLoading(true);
    try {
      const [attendance, clientRows, sessionRows] = await Promise.all([
        fetchAllRows('session_attendance', 'attendance_date, status, client_id', 'attendance_date'),
        fetchAllRows('clients', 'id, status', 'id'),
        fetchAllRows('sessions', 'client_id, valid_from, valid_until', 'client_id'),
      ]);

      setAttendanceRecords(attendance);
      setClients(clientRows);
      setSessions(sessionRows);
      setHasLoaded(true);
    } catch (error) {
      console.error('Error fetching session trends:', error);
      toast({
        title: 'Failed to load session trends',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (!isVisible || hasLoaded) return;
    fetchTrendData();
  }, [isVisible, hasLoaded, fetchTrendData]);

  const chartData = useMemo(() => {
    if (!attendanceRecords.length) return { series: [], bucketMode: viewMode };

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = findEarliestAttendanceDate(attendanceRecords) || today;
    const { buckets, bucketMode } = generateBuckets(startDate, today, viewMode);
    const sessionCounts = aggregateSessionCounts(attendanceRecords, bucketMode);
    const clientRanges = buildClientActiveRanges(clients, sessions, today);
    const activeClientsByBucket = aggregateActiveClients(clientRanges, buckets);
    const series = buildChartSeries(buckets, sessionCounts, activeClientsByBucket);

    return { series, bucketMode };
  }, [attendanceRecords, clients, sessions, viewMode]);

  const currentWeekSummary = useMemo(() => {
    if (!attendanceRecords.length) return null;

    const weekKey = getCurrentWeekBucketKey(new Date());
    const sessionCounts = aggregateSessionCounts(attendanceRecords, 'weekly');
    const counts = sessionCounts[weekKey];
    if (!counts) return null;

    return { weekKey, ...counts };
  }, [attendanceRecords]);

  const toggleLine = (key) => {
    setVisibleLines((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const showSessionsAxis = visibleLines.total || visibleLines.present || visibleLines.cancelled;
  const showClientsAxis = visibleLines.activeClients;

  if (!isVisible) return null;

  return (
    <VStack align="stretch" spacing={4}>
      <HStack justify="space-between" flexWrap="wrap" gap={3}>
        <Heading size="md">Session Trends</Heading>
        <ButtonGroup size="sm" isAttached variant="outline">
          {VIEW_MODES.map((mode) => (
            <Button
              key={mode}
              onClick={() => setViewMode(mode)}
              colorScheme={viewMode === mode ? 'blue' : 'gray'}
              variant={viewMode === mode ? 'solid' : 'outline'}
            >
              {VIEW_MODE_LABELS[mode]}
            </Button>
          ))}
        </ButtonGroup>
      </HStack>

      {currentWeekSummary && (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
          <Stat bg="blue.50" p={3} borderRadius="md">
            <StatLabel>This Week Total</StatLabel>
            <StatNumber>{currentWeekSummary.total}</StatNumber>
            <StatHelpText>session hours</StatHelpText>
          </Stat>
          <Stat bg="green.50" p={3} borderRadius="md">
            <StatLabel>Present</StatLabel>
            <StatNumber>{currentWeekSummary.present}</StatNumber>
          </Stat>
          <Stat bg="orange.50" p={3} borderRadius="md">
            <StatLabel>Cancelled</StatLabel>
            <StatNumber>{currentWeekSummary.cancelled}</StatNumber>
          </Stat>
          <Stat bg="gray.50" p={3} borderRadius="md">
            <StatLabel>Week Of</StatLabel>
            <StatNumber fontSize="lg">{currentWeekSummary.weekKey}</StatNumber>
          </Stat>
        </SimpleGrid>
      )}

      <HStack spacing={4} flexWrap="wrap">
        {LINE_CONFIG.map(({ key, label }) => (
          <Checkbox
            key={key}
            isChecked={visibleLines[key]}
            onChange={() => toggleLine(key)}
          >
            {label}
          </Checkbox>
        ))}
        <Tooltip
          label="Total = all attendance records. Cancelled includes late cancellations. Active Clients = roster size inferred from scheduled slot validity (not a formal status audit log)."
          hasArrow
        >
          <InfoIcon color="gray.400" />
        </Tooltip>
      </HStack>

      {viewMode === 'all-time' && chartData.series.length > 0 && (
        <Text fontSize="sm" color="gray.500">
          Auto granularity: {VIEW_MODE_LABELS[chartData.bucketMode] || chartData.bucketMode}
        </Text>
      )}

      {loading ? (
        <Center py={12}>
          <Spinner size="lg" />
        </Center>
      ) : chartData.series.length === 0 ? (
        <Center py={12}>
          <Text color="gray.500">No session attendance data found.</Text>
        </Center>
      ) : (
        <Box w="full" h="420px">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData.series} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11 }}
                interval="preserveStartEnd"
                minTickGap={24}
              />
              {showSessionsAxis && (
                <YAxis
                  yAxisId="sessions"
                  allowDecimals={false}
                  label={{ value: 'Sessions', angle: -90, position: 'insideLeft' }}
                />
              )}
              {showClientsAxis && (
                <YAxis
                  yAxisId="clients"
                  orientation="right"
                  allowDecimals={false}
                  label={{ value: 'Active Clients', angle: 90, position: 'insideRight' }}
                />
              )}
              <RechartsTooltip content={<CustomTooltip />} />
              <Legend />
              {LINE_CONFIG.map(({ key, label, color, yAxisId }) =>
                visibleLines[key] ? (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    name={label}
                    stroke={color}
                    strokeWidth={2}
                    dot={chartData.series.length <= 31}
                    yAxisId={yAxisId}
                    connectNulls
                  />
                ) : null
              )}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </VStack>
  );
};

export default SessionTrendsChart;
